import { HelpSections } from '@/api/consts'
import { Row } from '@/app/components/Flex/Flex'
import { Layout } from '@/app/components/Layout/Layout'
import { Motion } from '@/app/components/Motion/Motion'
import { UI } from '@/app/components/UI/UI'
import { useBack } from '@/app/hooks/useBack'
import type { FunctionalComponent } from 'preact'
import Markdown from 'react-markdown'
import { useParams } from 'wouter-preact'
import styles from './index.module.scss'

const sections = {
  [HelpSections.LoginExistingAccount]: `
## Как войти в существующий аккаунт?

Если у вас уже есть аккаунт, не создавайте новый.

1. На экране заполнения профиля нажмите **«Войти»**.
2. Выберите удобный способ входа.
3. Используйте тот же адрес электронной почты, с которым регистрировались или оплачивали доступ.

После входа восстановятся сохранённые данные, чаты и оставшиеся сообщения.
  `,
  [HelpSections.SwitchAccount]: `
## Как сменить аккаунт?

1. Перейдите в раздел **«Профиль»**.
2. Нажмите на значок выхода в правом верхнем углу.
3. На экране заполнения профиля нажмите **«Войти»**.
4. Выберите способ входа и авторизуйтесь в нужном аккаунте.

После входа отобразятся данные, чаты и сообщения, привязанные к этому аккаунту.
  `,
  [HelpSections.EmailCodeNotReceived]: `
## Не приходит код на почту?

1. Проверьте, правильно ли указан адрес электронной почты.
2. Загляните в папки **«Спам»**, **«Рассылки»** и **«Промоакции»**.
3. Подождите несколько минут и запросите код повторно.
4. Используйте код из последнего полученного письма.

Если письмо так и не пришло, [обратитесь в поддержку](mailto:sudokey@gmail.com?subject=Поддержка%20ZoeAstro) и укажите адрес, на который запрашивали код.
  `,
  [HelpSections.PurchaseAccountEmail]: `
## К какой почте привязывается покупка?

Покупка привязывается к адресу электронной почты, который вы указали при оплате.

Чтобы оплаченный пакет отображался, войдите в сервис с этим же адресом. Если вы используете другой аккаунт, выйдите из него и авторизуйтесь с почтой, указанной при покупке.

Если доступ не появился, [обратитесь в поддержку](mailto:sudokey@gmail.com?subject=Поддержка%20ZoeAstro) и укажите email, который использовали при оплате.
  `,
  [HelpSections.PaidPackageNotShowing]: `
## Оплаченный пакет не отображается

Убедитесь, что вы вошли с тем же адресом электронной почты, который указали при оплате.

Если вы используете другой аккаунт:

1. Перейдите в раздел **«Профиль»**.
2. Нажмите на значок выхода в правом верхнем углу.
3. На экране заполнения профиля нажмите **«Войти»**.
4. Авторизуйтесь с почтой, указанной при покупке.

После входа оплаченный пакет должен отобразиться автоматически.

Если доступ не появился, [обратитесь в поддержку](mailto:sudokey@gmail.com?subject=Поддержка%20ZoeAstro) и укажите email, использованный при оплате.
  `,
  [HelpSections.DataAndCalculationsMissing]: `
## Пропали данные и расчёты

Данные и расчёты сохраняются в аккаунте, в котором они были созданы. Если они пропали, возможно, вы вошли под другим аккаунтом.

1. Перейдите в раздел **«Профиль»**.
2. Выйдите из текущего аккаунта.
3. На экране заполнения профиля нажмите **«Войти»**.
4. Авторизуйтесь тем же способом и с той же почтой, которые использовали ранее.

После входа сохранённые данные и расчёты должны восстановиться. Если этого не произошло, [обратитесь в поддержку](mailto:sudokey@gmail.com?subject=Поддержка%20ZoeAstro).
  `,
}

export const HelpItemPage: FunctionalComponent = () => {
  const back = useBack('~/tools')
  const params = useParams<{ id: HelpSections }>()

  const text = sections[params.id]

  return (
    <Layout>
      <Layout.Content
        onBack={back}
      >
        <Motion>
          <Layout.Header>
            Помощь
          </Layout.Header>
          <Row gap={15}>
            <UI.Panel class={styles.content}>
              <Markdown>
                {text}
              </Markdown>
            </UI.Panel>

            <UI.Button
              size='l'
              type='secondary'
              htmlType='button'
              onClick={back}
            >
              Назад
            </UI.Button>
          </Row>
        </Motion>
      </Layout.Content>
    </Layout>
  )
}
